/**
 * Billing page — Bundle 4.
 *
 * Shows the current plan, billing status, usage vs limits,
 * feature availability, and upgrade / manage-subscription CTAs.
 *
 * Route: /billing  (protected — authenticated users only)
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api-client';
import { usePageMeta } from '../lib/page-meta';
import {
  WEB_PLAN_DEFINITIONS,
  formatPlanLimit,
  getPlanLabel,
  getPlanOrder,
  normalizePlanId,
} from '../lib/plans';
import BrandLogo from '../components/brand/BrandLogo';
import Container from '../components/ui/Container';
import Card from '../components/ui/Card';
import AlertBanner from '../components/ui/AlertBanner';
import { formatDate, formatNumber } from '../i18n/formatters';
import type { OrgBillingState, PlanLimits } from '@accestra/types';

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const C = {
  page:    'var(--acc-color-page)',
  text:    'var(--acc-color-text)',
  muted:   'var(--acc-color-text-muted)',
  border:  'var(--acc-color-border)',
  accent:  'var(--acc-color-primary)',
  success: 'var(--acc-color-success)',
  warning: 'var(--acc-color-warning)',
  danger:  'var(--acc-color-danger)',
  surface: 'var(--card-bg)',
  surfaceSecondary: 'var(--surface-secondary)',
  buttonSecondary: 'var(--button-secondary-bg)',
  warningSoft: 'var(--acc-badge-warning-bg)',
  warningBorder: 'var(--acc-badge-warning-border)',
  warningText: 'var(--acc-badge-warning-text)',
  infoSoft: 'var(--acc-badge-info-bg)',
  infoBorder: 'var(--acc-badge-info-border)',
  infoText: 'var(--acc-badge-info-text)',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function planColor(plan: string): string {
  const m: Record<string, string> = {
    free: C.muted,
    starter: 'var(--acc-color-info)',
    growth: C.accent,
    professional: C.accent,
    enterprise: 'var(--acc-color-gold-strong)',
  };
  return m[plan] ?? C.muted;
}

function billingStatusColor(status: string): string {
  const m: Record<string, string> = {
    active: C.success, trialing: 'var(--acc-color-info)', past_due: C.warning,
    canceled: C.danger, unpaid: C.danger, none: C.muted,
  };
  return m[status] ?? C.muted;
}

function billingStatusLabel(status: string): string {
  if (status === 'none') {
    return '—';
  }
  return status.replace(/_/g, ' ');
}

function Pill({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
      background: `${color}18`, color,
    }}>
      {label}
    </span>
  );
}

function StateTile({
  label,
  value,
  color = C.text,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 6,
        minHeight: 74,
        padding: '14px 16px',
        borderRadius: 12,
        border: `1px solid ${C.border}`,
        background: C.surface,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: C.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 15, fontWeight: 700, color, lineHeight: 1.45 }}>
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Usage bar
// ---------------------------------------------------------------------------

function UsageBar({
  label, used, limit, unit = '',
}: {
  label: string;
  used: number;
  limit: number;
  unit?: string;
}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const color = pct >= 100 ? C.danger : pct >= 80 ? C.warning : C.accent;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 13, fontWeight: 500, marginBottom: 6, color: C.text,
      }}>
        <span>{label}</span>
        <span style={{ color: pct >= 100 ? C.danger : C.muted }}>
          {formatNumber(used)}{unit} / {formatPlanLimit(limit)}{unit}
        </span>
      </div>
      <div style={{ height: 7, background: C.surfaceSecondary, borderRadius: 99, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`, borderRadius: 99,
          background: color, transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feature flag row
// ---------------------------------------------------------------------------

function FeatureRow({ label, enabled, upgradeHint }: { label: string; enabled: boolean; upgradeHint?: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 0', borderBottom: `1px solid ${C.border}`,
    }}>
      <span style={{ fontSize: 13, color: C.text }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {!enabled && upgradeHint && (
          <span style={{ fontSize: 11, color: C.muted }}>{upgradeHint}</span>
        )}
        {enabled
          ? <span style={{ color: C.success, fontSize: 16 }}>✓</span>
          : <span style={{ color: C.muted, fontSize: 16 }}>✗</span>
        }
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan upgrade card
// ---------------------------------------------------------------------------

interface PlanCard {
  id: string;
  name: string;
  tagline: string;
  priceMonthly: number | null;
  highlighted?: boolean;
}

type BillingPortalAction = 'manage' | 'change_plan' | 'cancel';

function actionStateKey(action: BillingPortalAction, targetPlan?: string): string {
  return targetPlan ? `${action}:${targetPlan}` : action;
}

function UpgradeCard({
  plan, currentPlan, onCheckout, onPlanChange, onPortalAction, redirectingAction, isTargeted, isScheduledDowngrade, isScheduledTarget,
}: {
  plan: PlanCard;
  currentPlan: string;
  onCheckout: (planId: string) => void;
  onPlanChange: (planId: 'free' | 'starter' | 'growth') => void;
  onPortalAction: (action: BillingPortalAction, targetPlan?: string) => void;
  redirectingAction: string | null;
  isTargeted: boolean;
  isScheduledDowngrade: boolean;
  isScheduledTarget: boolean;
}) {
  const { t } = useTranslation('billing');
  const isCurrent = plan.id === currentPlan;
  const isHigher  = getPlanOrder(plan.id) > getPlanOrder(currentPlan);
  const isLower   = getPlanOrder(plan.id) < getPlanOrder(currentPlan);
  const isEnterprise = plan.id === 'enterprise';
  const isBusy = redirectingAction !== null;
  const scheduledPlanLabel = getPlanLabel('free');

  if (isCurrent) {
    return (
      <div style={{
        border: `2px solid ${C.accent}`, borderRadius: 16, padding: 20,
        background: 'var(--acc-color-primary-soft)', flex: 1, minWidth: 160,
      }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: C.accent, marginBottom: 4 }}>{plan.name}</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>{plan.tagline}</div>
        <Pill label={t('planCard.currentPlan')} color={C.accent} />
        {plan.id !== 'free' && (
          <>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginTop: 14 }}>
              {isScheduledDowngrade
                ? t('actions.manageScheduledDowngrade', { plan: scheduledPlanLabel })
                : t('actions.managePlanBillingCancellation')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => onPortalAction('manage')}
                style={{
                  minHeight: 38,
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: C.buttonSecondary,
                  color: C.text,
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  opacity: isBusy ? 0.72 : 1,
                }}
              >
                {redirectingAction === actionStateKey('manage') ? t('planCard.redirecting') : t('actions.manageSubscription')}
              </button>
              <button
                type="button"
                disabled={isBusy}
                onClick={() => onPortalAction('change_plan', plan.id)}
                style={{
                  minHeight: 38,
                  padding: '8px 14px',
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: C.buttonSecondary,
                  color: C.text,
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  opacity: isBusy ? 0.72 : 1,
                }}
              >
                {redirectingAction === actionStateKey('change_plan', plan.id) ? t('planCard.redirecting') : t('actions.changePlan')}
              </button>
              {!isScheduledDowngrade && (
              <button
                type="button"
                disabled={isBusy}
                onClick={() => onPlanChange('free')}
                style={{
                  minHeight: 38,
                  padding: '8px 14px',
                    borderRadius: 10,
                    border: '1px solid var(--acc-badge-danger-border)',
                    background: 'var(--acc-badge-danger-bg)',
                    color: C.danger,
                    cursor: isBusy ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    opacity: isBusy ? 0.72 : 1,
                  }}
                >
                  {redirectingAction === actionStateKey('cancel', 'free') ? t('planCard.redirecting') : t('actions.cancelSubscription')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{
      border: isTargeted ? `2px solid ${C.accent}` : `1px solid ${C.border}`, borderRadius: 16, padding: 20,
      background: plan.highlighted ? 'var(--acc-color-gold-soft)' : C.surface,
      flex: 1, minWidth: 160,
      outline: plan.highlighted && !isTargeted ? `2px solid rgba(200, 155, 60, 0.28)` : 'none',
      outlineOffset: -2,
    }}>
      {(plan.highlighted || isTargeted) && (
        <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {isTargeted ? t('planCard.selected') : t('planCard.recommended')}
        </div>
      )}
      <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 4 }}>{plan.name}</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>{plan.tagline}</div>
      {plan.priceMonthly !== null && plan.priceMonthly > 0 && (
        <div style={{ fontSize: 20, fontWeight: 800, color: C.text, marginBottom: 14 }}>
          ${plan.priceMonthly}
          <span style={{ fontSize: 12, fontWeight: 400, color: C.muted }}>{t('labels.perMonthShort')}</span>
        </div>
      )}
      {isHigher && (
        isEnterprise ? (
          <a
            href="mailto:sales@ablecomply.com?subject=AbleComply%20Enterprise%20Enquiry"
            style={{
              display: 'block', textAlign: 'center', padding: '8px 0',
              background: 'var(--acc-color-primary-strong)', color: '#fff', borderRadius: 12,
              textDecoration: 'none', fontSize: 13, fontWeight: 600,
            }}
          >
            {t('planCard.contactSales')}
          </a>
        ) : (
          <button
            type="button"
            disabled={isBusy}
            onClick={() => (currentPlan === 'free' ? onCheckout(plan.id) : onPlanChange(plan.id as 'starter' | 'growth'))}
            style={{
              width: '100%', padding: '8px 0', border: 'none',
              background: plan.highlighted ? C.accent : 'var(--acc-color-primary-strong)',
              color: '#fff', borderRadius: 12, cursor: isBusy ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 600, opacity: isBusy ? 0.7 : 1,
            }}
          >
            {redirectingAction === actionStateKey('change_plan', plan.id) ? t('planCard.redirecting') : t('planCard.upgradeTo', { plan: plan.name })}
          </button>
        )
      )}
      {isLower && (
        isScheduledTarget ? (
          <div style={{
            width: '100%',
            minHeight: 38,
            padding: '8px 0',
            borderRadius: 12,
            border: `1px solid ${C.warningBorder}`,
            background: C.warningSoft,
            color: C.warningText,
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 600,
          }}>
            {t('labels.scheduledPlan', { plan: plan.name })}
          </div>
        ) : (
        <button
          type="button"
          disabled={isBusy}
          onClick={() => onPlanChange(plan.id as 'free' | 'starter' | 'growth')}
          style={{
            width: '100%',
            minHeight: 38,
            padding: '8px 0',
            borderRadius: 12,
            border: `1px solid ${C.border}`,
            background: C.buttonSecondary,
            color: C.text,
            cursor: isBusy ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            opacity: isBusy ? 0.72 : 1,
          }}
        >
          {redirectingAction === actionStateKey(plan.id === 'free' ? 'cancel' : 'change_plan', plan.id)
            ? t('planCard.redirecting')
            : plan.id === 'free'
              ? t('planCard.switchToPlan', { plan: plan.name })
              : t('planCard.downgrade')}
        </button>
        )
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function BillingPage() {
  const { t } = useTranslation('billing');
  const [searchParams] = useSearchParams();
  const [state,     setState]     = useState<OrgBillingState | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [redirectingAction, setRedirectingAction] = useState<string | null>(null);
  const [feedback,  setFeedback]  = useState<{ msg: string; ok: boolean } | null>(null);

  usePageMeta({
    title: t('meta.title'),
    description: t('meta.description'),
    canonicalPath: '/billing',
    robots: 'noindex,nofollow',
  });

  useEffect(() => {
    api.get<OrgBillingState>('/billing/me')
      .then(setState)
      .catch((err) => setError(err instanceof ApiError ? err.message : t('feedback.loadFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    if (success === '1') {
      setFeedback({ msg: t('feedback.checkoutSuccess'), ok: true });
      return;
    }
    if (cancelled === '1') {
      setFeedback({ msg: t('feedback.checkoutCancelled'), ok: false });
    }
  }, [searchParams, t]);

  const handleCheckoutUpgrade = async (planId: string) => {
    setRedirectingAction(actionStateKey('change_plan', planId));
    setFeedback(null);
    try {
      const result = await api.post<{ sessionUrl: string | null; stripeConfigured: boolean; contactSales: boolean }>(
        '/billing/checkout', { plan: planId },
      );
      if (result.contactSales) {
        window.location.assign('mailto:sales@ablecomply.com?subject=AbleComply%20Enterprise%20Enquiry');
      } else if (result.sessionUrl) {
        window.location.assign(result.sessionUrl);
      } else {
        setFeedback({
          msg: t('feedback.stripeUnavailable'),
          ok: false,
        });
      }
    } catch (err) {
      setFeedback({ msg: err instanceof ApiError ? err.message : t('feedback.upgradeFailed'), ok: false });
    } finally {
      setRedirectingAction(null);
    }
  };

  const handlePlanChange = async (planId: 'free' | 'starter' | 'growth') => {
    setRedirectingAction(actionStateKey(planId === 'free' ? 'cancel' : 'change_plan', planId));
    setFeedback(null);

    try {
      const nextState = await api.post<OrgBillingState>('/billing/change-plan', { plan: planId });
      setState(nextState);

      if (
        nextState.billing.cancelAtPeriodEnd
        && normalizePlanId(nextState.billing.scheduledPlan) === 'free'
      ) {
        const effectiveDate = nextState.billing.cancelAt ?? nextState.billing.currentPeriodEnd ?? null;
        const scheduledPlan = getPlanLabel(nextState.billing.scheduledPlan ?? planId);
        setFeedback({
          msg: effectiveDate
            ? t('feedback.downgradeScheduled', { plan: scheduledPlan, date: formatDate(effectiveDate) })
            : t('feedback.downgradeScheduledNoDate', { plan: scheduledPlan }),
          ok: true,
        });
      } else {
        setFeedback({
          msg: t('feedback.planChanged', { plan: getPlanLabel(nextState.plan) }),
          ok: true,
        });
      }
    } catch (err) {
      setFeedback({
        msg: err instanceof ApiError ? err.message : t('feedback.changePlanFailed'),
        ok: false,
      });
    } finally {
      setRedirectingAction(null);
    }
  };

  const handlePortal = async (action: BillingPortalAction, targetPlan?: string) => {
    setRedirectingAction(actionStateKey(action, targetPlan));
    setFeedback(null);
    try {
      const query = new URLSearchParams();
      if (action !== 'manage') {
        query.set('action', action);
      }
      if (targetPlan) {
        query.set('targetPlan', targetPlan);
      }
      const result = await api.get<{ portalUrl: string | null; stripeConfigured: boolean }>(
        query.size > 0 ? `/billing/portal?${query.toString()}` : '/billing/portal',
      );
      if (result.portalUrl) {
        window.location.assign(result.portalUrl);
      } else {
        setFeedback({ msg: t('feedback.portalUnavailable'), ok: false });
      }
    } catch (err) {
      setFeedback({ msg: err instanceof ApiError ? err.message : t('feedback.portalOpenFailed'), ok: false });
    } finally {
      setRedirectingAction(null);
    }
  };

  const limits: PlanLimits | null = state?.limits ?? null;
  const currentPlanId = normalizePlanId(state?.plan) ?? 'free';
  const currentPlanLabel = getPlanLabel(state?.plan);
  const scheduledPlanId = normalizePlanId(state?.billing.scheduledPlan);
  const scheduledPlanLabel = scheduledPlanId ? getPlanLabel(scheduledPlanId) : null;
  const scheduledChangeDate = state?.billing.cancelAt ?? state?.billing.currentPeriodEnd ?? null;
  const scheduledChangeDateLabel = scheduledChangeDate ? formatDate(scheduledChangeDate) : null;
  const hasScheduledFreeDowngrade = Boolean(
    state &&
    currentPlanId !== 'free' &&
    state.billing.cancelAtPeriodEnd &&
    scheduledPlanId === 'free',
  );
  const targetedPlanId = normalizePlanId(searchParams.get('plan'));
  const targetedPlanLabel = targetedPlanId ? getPlanLabel(targetedPlanId) : null;
  const selectedFromPricing = searchParams.get('source') === 'pricing' && targetedPlanLabel !== null;
  const effectiveScheduledPlanLabel = scheduledPlanLabel ?? getPlanLabel('free');
  const billingStatusLabelText = state
    ? t(`statuses.${state.billing.billingStatus}` as const, { defaultValue: billingStatusLabel(state.billing.billingStatus) })
    : billingStatusLabel('none');
  const periodSummaryLabel = state && (state.billing.billingStatus === 'active' || state.billing.billingStatus === 'trialing')
    ? t('summary.nextRenewal')
    : t('summary.currentPeriodEnd');
  const planCards: PlanCard[] = WEB_PLAN_DEFINITIONS.map((plan) => ({
    id: plan.id,
    name: getPlanLabel(plan.id),
    tagline: t(`planCard.taglines.${plan.id}`),
    priceMonthly: plan.priceMonthly,
    highlighted: plan.id === 'growth',
  }));

  return (
    <Container maxWidth={1280} style={{ fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ padding: '32px 0' }}>

          {/* Page header */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ marginBottom: 18 }}>
              <BrandLogo variant="marketing" />
            </div>
            <h1 style={{ fontSize: 30, fontWeight: 800, color: C.text, margin: 0, marginBottom: 8, letterSpacing: '-0.04em' }}>
              {t('header.title')}
            </h1>
            <p style={{ fontSize: 15, color: C.muted, margin: 0, lineHeight: 1.7 }}>
              {t('header.description')}
              {' '}
              <Link to="/pricing" style={{ color: C.accent, textDecoration: 'none' }}>{t('header.viewPricing')}</Link>
            </p>
          </div>

          {error && (
            <div style={{ background: 'var(--acc-badge-danger-bg)', border: '1px solid var(--acc-badge-danger-border)', borderRadius: 8, padding: '12px 16px', color: 'var(--acc-badge-danger-text)', fontSize: 13, marginBottom: 20 }}>
              {error}
            </div>
          )}

          {feedback && (
            <div style={{ marginBottom: 20 }}>
            <AlertBanner variant={feedback.ok ? 'success' : 'danger'}>
              <span>{feedback.msg}</span>
            </AlertBanner>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: C.muted, fontSize: 14 }}>{t('loading')}</div>
          ) : state && (

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, alignItems: 'start' }}>

              {/* Left column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Current plan + billing status */}
                <Card>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 }}>
                    {t('sections.currentPlan')}
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: 12,
                      marginBottom: hasScheduledFreeDowngrade || state.isTrialing ? 16 : 0,
                    }}
                  >
                    <StateTile
                      label={t('summary.currentPlan')}
                      value={currentPlanLabel}
                      color={planColor(state.plan)}
                    />
                    <StateTile
                      label={t('summary.status')}
                      value={billingStatusLabelText}
                      color={billingStatusColor(state.billing.billingStatus)}
                    />
                    {hasScheduledFreeDowngrade ? (
                      <>
                        <StateTile
                          label={t('summary.activeUntil')}
                          value={scheduledChangeDateLabel ?? t('labels.periodEndPending')}
                          color={C.warning}
                        />
                        <StateTile
                          label={t('summary.scheduledNextPlan')}
                          value={effectiveScheduledPlanLabel}
                          color={C.warning}
                        />
                      </>
                    ) : (
                      state.billing.currentPeriodEnd && (
                        <StateTile
                          label={periodSummaryLabel}
                          value={formatDate(state.billing.currentPeriodEnd)}
                        />
                      )
                    )}
                  </div>

                  {/* Trial countdown */}
                  {state.isTrialing && state.trialDaysRemaining !== null && (
                    <div style={{
                      background: C.infoSoft, border: `1px solid ${C.infoBorder}`, borderRadius: 8,
                      padding: '10px 14px', fontSize: 13, color: C.infoText, marginBottom: 16,
                    }}>
                      ⏱ <strong>{t('labels.trialEndsIn', { count: state.trialDaysRemaining })}</strong>
                      {state.billing.trialEndsAt && (
                        <span style={{ color: C.infoText }}>
                          {' '}({formatDate(state.billing.trialEndsAt)})
                        </span>
                      )}
                    </div>
                  )}

                  {/* Period end */}
                  {hasScheduledFreeDowngrade && (
                    <div style={{
                      background: C.warningSoft,
                      border: `1px solid ${C.warningBorder}`,
                      borderRadius: 10,
                      padding: '12px 14px',
                      marginTop: 4,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                        <Pill label={t('scheduled.badge')} color={C.warningText} />
                        {scheduledChangeDateLabel && (
                          <span style={{ fontSize: 12, fontWeight: 600, color: C.warningText }}>
                            {t('scheduled.effectiveDate', { date: scheduledChangeDateLabel })}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.warningText, marginBottom: 6 }}>
                        {t('scheduled.title', { plan: effectiveScheduledPlanLabel })}
                      </div>
                      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>
                        {scheduledChangeDateLabel
                          ? t('scheduled.description', {
                              currentPlan: currentPlanLabel,
                              scheduledPlan: effectiveScheduledPlanLabel,
                              date: scheduledChangeDateLabel,
                            })
                          : t('scheduled.descriptionWithoutDate', {
                              currentPlan: currentPlanLabel,
                              scheduledPlan: effectiveScheduledPlanLabel,
                            })}
                      </div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 8, lineHeight: 1.6 }}>
                        {t('scheduled.manageHint')}
                      </div>
                    </div>
                  )}
                </Card>

                <Card>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                    {t('sections.planPosture')}
                  </div>
                  <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7, marginBottom: 12 }}>
                    {currentPlanId === 'free'
                      ? t('posture.free')
                      : currentPlanId === 'starter'
                        ? t('posture.starter')
                        : currentPlanId === 'growth'
                          ? t('posture.growth')
                          : t('posture.enterprise')}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                    {t('posture.downgrade')}
                  </div>
                </Card>

                {/* Usage */}
                <Card>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
                    {t('sections.usage')}
                  </div>
                  {limits && (
                    <>
                      <UsageBar label={t('labels.sites')} used={state.usage.sitesCount} limit={limits.maxSites} />
                      <UsageBar label={t('labels.scans30Days')} used={state.usage.scansThisMonth} limit={limits.maxScansPerMonth} />
                      <UsageBar label={t('labels.teamMembers')} used={state.usage.teamMembersCount} limit={limits.maxTeamMembers} />
                      <div style={{ fontSize: 12, color: C.muted, display: 'grid', gap: 6 }}>
                        <span>
                          {state.usage.sitesCount >= limits.maxSites || state.usage.teamMembersCount >= limits.maxTeamMembers || state.usage.scansThisMonth >= limits.maxScansPerMonth
                            ? t('labels.usageLimitReached')
                            : state.usage.sitesCount >= limits.maxSites * 0.8 || state.usage.teamMembersCount >= limits.maxTeamMembers * 0.8 || state.usage.scansThisMonth >= limits.maxScansPerMonth * 0.8
                              ? t('labels.usageNearLimit')
                              : t('labels.usageAvailable')}
                        </span>
                      </div>
                    </>
                  )}
                </Card>

                {/* Feature flags */}
                <Card>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    {t('sections.features')}
                  </div>
                  {limits && (
                    <>
                      <FeatureRow label={t('labels.scheduledScans')} enabled={limits.scheduledScansEnabled} upgradeHint={t('labels.starterPlus')} />
                      <FeatureRow label={t('labels.alertsNotifications')} enabled={limits.alertsEnabled} upgradeHint={t('labels.starterPlus')} />
                      <FeatureRow label={t('labels.authenticatedScanning')} enabled={limits.authenticatedScanningEnabled} upgradeHint={t('labels.growthPlus')} />
                      <FeatureRow label={t('labels.complianceExport')} enabled={limits.complianceExportEnabled} upgradeHint={t('labels.growthPlus')} />
                      <FeatureRow label={t('labels.regressionHistory')} enabled={limits.regressionHistoryDepth > 0} />
                      <div style={{ paddingTop: 10, fontSize: 12, color: C.muted }}>
                        {t('labels.historyDepth', { count: limits.regressionHistoryDepth })}
                        {' · '}
                        {t('labels.pagesPerScan', { count: limits.maxPagesPerScan })}
                      </div>
                    </>
                  )}
                </Card>
              </div>

              {/* Right column — upgrade */}
              <div>
                <Card>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
                    {t('sections.plans')}
                  </div>
                  {selectedFromPricing && targetedPlanLabel && (
                    <div style={{
                      background: C.infoSoft, border: `1px solid ${C.infoBorder}`, borderRadius: 7,
                      padding: '10px 14px', fontSize: 12, color: C.infoText, marginBottom: 16,
                    }}>
                      {t('labels.selectedFromPricing', { plan: targetedPlanLabel })}
                    </div>
                  )}
                  {!state.stripeConfigured && (
                    <div style={{
                      background: C.warningSoft, border: `1px solid ${C.warningBorder}`, borderRadius: 7,
                      padding: '10px 14px', fontSize: 12, color: C.warningText, marginBottom: 16,
                    }}>
                      {t('labels.stripeNotice', { email: 'support@ablecomply.com' })}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {planCards.map((plan) => (
                      <UpgradeCard
                        key={plan.id}
                        plan={plan}
                        currentPlan={currentPlanId}
                        onCheckout={(id) => void handleCheckoutUpgrade(id)}
                        onPlanChange={(id) => void handlePlanChange(id)}
                        onPortalAction={(action, targetPlan) => void handlePortal(action, targetPlan)}
                        redirectingAction={redirectingAction}
                        isTargeted={targetedPlanId === plan.id}
                        isScheduledDowngrade={hasScheduledFreeDowngrade && plan.id === currentPlanId}
                        isScheduledTarget={hasScheduledFreeDowngrade && scheduledPlanId === plan.id}
                      />
                    ))}
                  </div>
                </Card>
              </div>

            </div>
          )}
        </div>
      </Container>
  );
}
